import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getSetupWizardOverview, getSetupWizardSessions } from '../api/setupWizardApi';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { IconButton } from '../components/ui/IconButton';
import { formatSetupStepTitle } from '../utils/systemLabels';

export function SetupWizardDashboardPage() {
  const { t } = useTranslation(['setupWizard', 'common', 'statuses']);
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const emptyValue = t('common:empty_value');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [overviewResponse, sessionsResponse] = await Promise.all([
        getSetupWizardOverview(),
        getSetupWizardSessions({ per_page: 20 }),
      ]);
      setOverview(overviewResponse.data || overviewResponse);
      setSessions(sessionsResponse.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const data = overview || {};
  const recordCount = sessions.length;

  const metricCards = useMemo(
    () => [
      { key: 'total', label: t('setupWizard:dashboard.metrics.total'), value: data.total_sessions ?? 0 },
      { key: 'in_progress', label: t('setupWizard:dashboard.metrics.in_progress'), value: data.in_progress_count ?? 0 },
      { key: 'completed', label: t('setupWizard:dashboard.metrics.completed'), value: data.completed_count ?? 0 },
      { key: 'blocked', label: t('setupWizard:dashboard.metrics.blocked'), value: data.blocked_count ?? 0 },
      {
        key: 'required_incomplete',
        label: t('setupWizard:dashboard.metrics.required_incomplete'),
        value: data.required_incomplete_count ?? 0,
      },
      {
        key: 'enabled',
        label: t('setupWizard:dashboard.metrics.enabled'),
        value: data.enabled ? t('statuses:yes') : t('statuses:no'),
      },
    ],
    [
      data.blocked_count,
      data.completed_count,
      data.enabled,
      data.in_progress_count,
      data.required_incomplete_count,
      data.total_sessions,
      t,
    ],
  );

  const columns = useMemo(
    () => [
      {
        id: 'tenant_id',
        label: t('setupWizard:dashboard.columns.tenant'),
        render: (row) => (
          row.tenant_name
          || t('setupWizard:dashboard.tenant_link', { id: row.tenant_id })
          || emptyValue
        ),
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? <StatusBadge status={row.status} category="setup_session" size="Sm" />
            : emptyValue
        ),
      },
      {
        id: 'current_step_code',
        label: t('setupWizard:dashboard.columns.current_step'),
        render: (row) => (
          row.current_step_code
            ? formatSetupStepTitle(row.current_step_code, t)
            : emptyValue
        ),
      },
      {
        id: 'progress_percent',
        label: t('setupWizard:dashboard.columns.progress'),
        render: (row) => `${Math.round(row.progress?.progress_percent ?? 0)}%`,
      },
      {
        id: 'required',
        label: t('setupWizard:dashboard.columns.required'),
        render: (row) => (
          <StatusBadge
            status={row.required ? 'yes' : 'no'}
            category="generic"
            apiLabel={row.required ? t('statuses:yes') : t('statuses:no')}
            size="Sm"
            tone={row.required ? 'Warning' : 'Neutral'}
          />
        ),
      },
      {
        id: 'updated_at',
        label: t('setupWizard:dashboard.columns.updated'),
        render: (row) => {
          if (!row.updated_at) return emptyValue;
          const date = new Date(row.updated_at);
          return Number.isNaN(date.getTime()) ? row.updated_at : date.toLocaleString();
        },
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (row) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('common:view_detail')}
            onClick={(event) => {
              event.stopPropagation();
              if (row.tenant_id) {
                navigate(`/tenants/${row.tenant_id}`);
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, navigate, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('setupWizard:dashboard.title')}
        subtitle={t('setupWizard:dashboard.subtitle')}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={load} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('setupWizard:dashboard.load_failed')}
        </Alert>
      )}

      {loading ? (
        <>
          <SectionCard variant="Default" sx={{ mb: 2.5 }}>
            <LoadingSkeleton type="CardGrid" rows={6} />
          </SectionCard>
          <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('setupWizard:dashboard.recent_sessions')}
            />
            <LoadingSkeleton type="Table" embedded rows={3} columns={7} />
          </SectionCard>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(6, minmax(0, 1fr))',
              },
              gap: 2,
              width: '100%',
              mb: 2.5,
            }}
          >
            {metricCards.map((card) => (
              <MetricCard
                key={card.key}
                value={card.value}
                label={card.label}
              />
            ))}
          </Box>

          <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('setupWizard:dashboard.recent_sessions')}
              meta={(
                <StatusBadge
                  status="list"
                  category="generic"
                  apiLabel={t('setupWizard:dashboard.toolbar_count', { count: recordCount })}
                  size="Sm"
                  tone="Neutral"
                />
              )}
            />

            <DataTable
              type="Default"
              embedded
              columns={columns}
              rows={sessions}
              getRowKey={(row) => row.id}
              emptyMessage={t('setupWizard:dashboard.no_sessions')}
              sx={{
                overflowX: 'auto',
                width: '100%',
                '& table': {
                  width: '100%',
                  minWidth: '100%',
                },
              }}
            />
          </SectionCard>
        </>
      )}
    </Box>
  );
}
